import { CapacitorHttp } from '@capacitor/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { isCapacitorNative } from './service/platform/capacitor-platform';
import { LoggerService } from './service/utils/logger.service';

export type OpenIdDiscoveryDoc = Record<string, unknown>;

/** True when the WebView origin and API host differ (CORS / status 0 without native HTTP). */
export function needsNativeDiscoveryFetch(discoveryUrl: string): boolean {
  if (!isCapacitorNative()) {
    return false;
  }
  if (!discoveryUrl.startsWith('http://') && !discoveryUrl.startsWith('https://')) {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const apiOrigin = new URL(discoveryUrl).origin;
    return apiOrigin !== window.location.origin;
  } catch {
    return isCapacitorNative();
  }
}

export async function fetchOpenIdDiscoveryDocument(url: string): Promise<OpenIdDiscoveryDoc> {
  if (needsNativeDiscoveryFetch(url)) {
    LoggerService.log('[OIDC] Loading discovery via Capacitor native HTTP', { url });
    const response = await CapacitorHttp.get({
      url,
      headers: { Accept: 'application/json' },
      responseType: 'json',
    });
    if (response.status < 200 || response.status >= 300) {
      throw { status: response.status, message: `Discovery HTTP ${response.status}` };
    }
    const data = response.data;
    if (data && typeof data === 'object') {
      return data as OpenIdDiscoveryDoc;
    }
    if (typeof data === 'string') {
      return JSON.parse(data) as OpenIdDiscoveryDoc;
    }
    throw new Error('Discovery response was not JSON');
  }

  const res = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
  if (!res.ok) {
    throw { status: res.status, message: `Discovery HTTP ${res.status}` };
  }
  return (await res.json()) as OpenIdDiscoveryDoc;
}

/**
 * Apply a discovery document like angular-oauth2-oidc's loadDiscoveryDocument (without HttpClient).
 */
export async function applyOpenIdDiscoveryDocument(
  oauth: OAuthService,
  doc: OpenIdDiscoveryDoc
): Promise<void> {
  const svc = oauth as OAuthService & {
    loadJwks(): Promise<unknown>;
    discoveryDocumentLoaded: boolean;
    discoveryDocumentLoadedSubject: { next(v: unknown): void };
    eventsSubject: { next(v: unknown): void };
    loginUrl: string;
    logoutUrl: string;
    grantTypesSupported: string[];
    issuer: string;
    tokenEndpoint: string;
    userinfoEndpoint: string;
    jwksUri: string;
    sessionCheckIFrameUrl: string;
    revocationEndpoint: string;
    sessionChecksEnabled: boolean;
    restartSessionChecksIfStillLoggedIn(): void;
  };

  svc.loginUrl = String(doc['authorization_endpoint'] ?? '');
  svc.logoutUrl = String(doc['end_session_endpoint'] ?? svc.logoutUrl ?? '');
  svc.grantTypesSupported = (doc['grant_types_supported'] as string[]) ?? [];
  svc.issuer = String(doc['issuer'] ?? svc.issuer ?? '');
  svc.tokenEndpoint = String(doc['token_endpoint'] ?? '');
  svc.userinfoEndpoint = String(doc['userinfo_endpoint'] ?? svc.userinfoEndpoint ?? '');
  svc.jwksUri = String(doc['jwks_uri'] ?? '');
  svc.sessionCheckIFrameUrl = String(doc['check_session_iframe'] ?? svc.sessionCheckIFrameUrl ?? '');
  svc.revocationEndpoint = String(doc['revocation_endpoint'] ?? svc.revocationEndpoint ?? '');
  svc.discoveryDocumentLoaded = true;
  svc.discoveryDocumentLoadedSubject.next(doc);

  if (svc.sessionChecksEnabled) {
    svc.restartSessionChecksIfStillLoggedIn();
  }

  if (svc.jwksUri) {
    await svc.loadJwks();
  }
}
